import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpenCheck, Eye, Plus, Trash2 } from 'lucide-react';
import { AlertBanner, Button, Modal, Skeleton, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/FormCard';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import type { usePerms } from '@/hooks/usePerms';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';
import { toast } from '@/store/toastStore';
import {
  type Leasing, type LeasingInput, type NatureApport, type TraitementLeasing,
  comptabiliserAcquisitionLeasing, createLeasing, deleteLeasing, getLeasing, listImmobilisations,
  listLeasing, payerEcheanceLeasing,
} from '@/services/immobilisations';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const today = () => new Date().toISOString().slice(0, 10);
const TRAIT_UI: Record<TraitementLeasing, { label: string; status: StatusKey }> = {
  IMMOBILISATION: { label: 'Immobilisation', status: 'done' },
  LOCATION_SIMPLE: { label: 'Location simple', status: 'pending' },
};

type Form = {
  immobilisation_id: string; organisme_bailleur: string; valeur_financee: number;
  apport_montant: number; apport_nature: NatureApport; duree_mois: number; echeance_mensuelle: number;
  taux_pct: number; date_debut: string; date_levee_option: string; valeur_residuelle_option: number;
  traitement: TraitementLeasing;
};
const vide = (): Form => ({
  immobilisation_id: '', organisme_bailleur: '', valeur_financee: 0, apport_montant: 0,
  apport_nature: 'ACOMPTE', duree_mois: 60, echeance_mensuelle: 0, taux_pct: 6.5,
  date_debut: today(), date_levee_option: '', valeur_residuelle_option: 0, traitement: 'IMMOBILISATION',
});

export function LeasingTab({ perms }: { perms: ReturnType<typeof usePerms> }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(vide());
  const [detailId, setDetailId] = useState<string | null>(null);
  const set = (p: Partial<Form>) => setForm((f) => ({ ...f, ...p }));

  const { data: contrats, isLoading } = useQuery({ queryKey: ['leasing'], queryFn: listLeasing });
  const { data: immos } = useQuery({ queryKey: ['immobilisations'], queryFn: listImmobilisations });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['leasing'] });

  const createMut = useMutation({
    mutationFn: () => {
      const payload: LeasingInput = {
        immobilisation_id: form.immobilisation_id || null, organisme_bailleur: form.organisme_bailleur || null,
        valeur_financee: Number(form.valeur_financee) || 0, apport_montant: Number(form.apport_montant) || 0,
        apport_nature: form.apport_nature, duree_mois: Number(form.duree_mois) || 0,
        echeance_mensuelle: Number(form.echeance_mensuelle) || 0, taux_annuel: (Number(form.taux_pct) || 0) / 100,
        date_debut: form.date_debut || null, date_levee_option: form.date_levee_option || null,
        valeur_residuelle_option: Number(form.valeur_residuelle_option) || 0, traitement: form.traitement,
      };
      return createLeasing(payload);
    },
    onSuccess: () => { invalidate(); setOpen(false); setForm(vide()); toast.success('Contrat de crédit-bail créé (échéancier généré).'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({ mutationFn: deleteLeasing, onSuccess: () => { invalidate(); toast.success('Contrat supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  const immoLabel = (id: string | null) => immos?.find((i) => i.id === id)?.code ?? null;

  return (
    <div className="space-y-4">
      <AlertBanner variant="info" title="Crédit-bail (location-financement)">
        Le traitement comptable (immobilisation à l'actif + dette, ou location simple en charge) est <b>tranché par le comptable</b> via le champ « traitement ». Les écritures sont générées en <b>brouillon</b> sur déclenchement.
      </AlertBanner>

      {perms.canModifier && <Button onClick={() => { setForm(vide()); setOpen(true); }}><Plus /> Nouveau contrat de crédit-bail</Button>}

      {isLoading ? <Skeleton className="h-48 w-full" /> : (contrats ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">Aucun contrat de crédit-bail.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-3 label-caps">Bailleur</th><th className="px-4 py-3 label-caps">Bien</th>
              <th className="px-4 py-3 text-right label-caps">Financé</th><th className="px-4 py-3 text-right label-caps">Apport</th>
              <th className="px-4 py-3 text-right label-caps">Durée</th><th className="px-4 py-3 text-right label-caps">Dette restante</th>
              <th className="px-4 py-3 label-caps">Traitement</th><th className="px-4 py-3" /></tr></thead>
            <tbody>
              {(contrats ?? []).map((c: Leasing) => {
                const t = TRAIT_UI[c.traitement];
                return (
                  <tr key={c.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 font-medium text-content-primary">{c.organisme_bailleur ?? '—'}</td>
                    <td className="px-4 py-2 text-content-secondary">{immoLabel(c.immobilisation_id) ?? <span className="italic text-content-muted">—</span>}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatDZD(Number(c.valeur_financee), { decimals: 0 })}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatDZD(Number(c.apport_montant), { decimals: 0 })}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{c.duree_mois} m</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{formatDZD(Number(c.dette_residuelle), { decimals: 0 })}</td>
                    <td className="px-4 py-2"><StatusBadge status={t.status} label={t.label} /></td>
                    <td className="px-4 py-2"><div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Échéancier" onClick={() => setDetailId(c.id)} aria-label="Détail"><Eye className="size-4" /></Button>
                      {perms.canSupprimer && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm('Supprimer ce contrat ?')) delMut.mutate(c.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
                    </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title="Nouveau contrat de crédit-bail" size="lg"
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={() => createMut.mutate()} loading={createMut.isPending} disabled={!form.valeur_financee || !form.duree_mois}>Créer + générer l'échéancier</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Traitement comptable">
            <select className={selectCls} value={form.traitement} onChange={(e) => set({ traitement: e.target.value as TraitementLeasing })}>
              <option value="IMMOBILISATION">Immobilisation (actif + dette)</option>
              <option value="LOCATION_SIMPLE">Location simple (loyers en charge)</option>
            </select>
          </FormField>
          <FormField label="Immobilisation liée (si immobilisé)">
            <select className={selectCls} value={form.immobilisation_id} onChange={(e) => set({ immobilisation_id: e.target.value })}>
              <option value="">— Aucune —</option>
              {(immos ?? []).filter((i) => i.statut === 'EN_SERVICE').map((i) => <option key={i.id} value={i.id}>{i.code} · {i.libelle}</option>)}
            </select>
          </FormField>
          <FormField label="Organisme bailleur"><Input value={form.organisme_bailleur} onChange={(e) => set({ organisme_bailleur: e.target.value })} placeholder="Banque / société de leasing" /></FormField>
          <FormField label="Valeur financée (dette)"><Input type="number" step="1" value={form.valeur_financee} onChange={(e) => set({ valeur_financee: Number(e.target.value) })} /></FormField>
          <FormField label="Apport initial"><Input type="number" step="1" value={form.apport_montant} onChange={(e) => set({ apport_montant: Number(e.target.value) })} /></FormField>
          <FormField label="Nature de l'apport">
            <select className={selectCls} value={form.apport_nature} onChange={(e) => set({ apport_nature: e.target.value as NatureApport })}>
              <option value="ACOMPTE">Acompte</option>
              <option value="PREMIER_LOYER_MAJORE">1er loyer majoré</option>
            </select>
          </FormField>
          <FormField label="Durée (mois)"><Input type="number" step="1" value={form.duree_mois} onChange={(e) => set({ duree_mois: Number(e.target.value) })} /></FormField>
          <FormField label="Taux annuel (%)"><Input type="number" step="0.1" value={form.taux_pct} onChange={(e) => set({ taux_pct: Number(e.target.value) })} /></FormField>
          <FormField label="Échéance mensuelle (0 = calculée)"><Input type="number" step="1" value={form.echeance_mensuelle} onChange={(e) => set({ echeance_mensuelle: Number(e.target.value) })} /></FormField>
          <FormField label="Date de début"><Input type="date" value={form.date_debut} onChange={(e) => set({ date_debut: e.target.value })} /></FormField>
          <FormField label="Date de levée d'option"><Input type="date" value={form.date_levee_option} onChange={(e) => set({ date_levee_option: e.target.value })} /></FormField>
          <FormField label="Valeur résiduelle (option)"><Input type="number" step="1" value={form.valeur_residuelle_option} onChange={(e) => set({ valeur_residuelle_option: Number(e.target.value) })} /></FormField>
        </div>
        <p className="mt-2 text-xs text-content-muted">L'apport n'est pas une charge. L'échéancier (capital/intérêts) est calculé par le serveur en annuités constantes.</p>
      </Modal>

      {detailId && <DetailLeasing id={detailId} perms={perms} onClose={() => setDetailId(null)} onChange={invalidate} />}
    </div>
  );
}

function DetailLeasing({ id, perms, onClose, onChange }: { id: string; perms: ReturnType<typeof usePerms>; onClose: () => void; onChange: () => void }) {
  const qc = useQueryClient();
  const { data: lb, isLoading } = useQuery({ queryKey: ['leasing', id], queryFn: () => getLeasing(id) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['leasing', id] }); onChange(); };
  const acqMut = useMutation({ mutationFn: () => comptabiliserAcquisitionLeasing(id), onSuccess: (r) => { refresh(); toast.success(r.detail); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const payMut = useMutation({ mutationFn: (eid: string) => payerEcheanceLeasing(eid), onSuccess: (r) => { refresh(); toast.success(r.detail); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Crédit-bail — ${lb?.organisme_bailleur ?? ''}`} size="xl">
      {isLoading || !lb ? <Skeleton className="h-64 w-full" /> : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="rounded bg-surface-secondary px-2 py-1">Financé <b className="tabular-nums">{formatDZD(Number(lb.valeur_financee), { decimals: 0 })}</b></span>
            <span className="rounded bg-surface-secondary px-2 py-1">Apport <b className="tabular-nums">{formatDZD(Number(lb.apport_montant), { decimals: 0 })}</b></span>
            <span className="rounded bg-surface-secondary px-2 py-1">Dette restante <b className="tabular-nums">{formatDZD(Number(lb.dette_residuelle), { decimals: 0 })}</b></span>
            <StatusBadge status={TRAIT_UI[lb.traitement].status} label={TRAIT_UI[lb.traitement].label} />
          </div>
          {perms.canModifier && lb.traitement === 'IMMOBILISATION' && lb.immobilisation_id && (
            <Button variant="secondary" size="sm" loading={acqMut.isPending} onClick={() => acqMut.mutate()}><BookOpenCheck className="size-4" /> Comptabiliser l'acquisition (brouillon)</Button>
          )}
          <div className="max-h-96 overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-primary"><tr className="border-b border-border text-left">
                <th className="px-3 py-2 label-caps">N°</th><th className="px-3 py-2 label-caps">Date</th>
                <th className="px-3 py-2 text-right label-caps">Échéance</th><th className="px-3 py-2 text-right label-caps">Capital</th>
                <th className="px-3 py-2 text-right label-caps">Intérêts</th><th className="px-3 py-2 text-right label-caps">Restant dû</th>
                <th className="px-3 py-2 label-caps">Statut</th><th className="px-3 py-2" /></tr></thead>
              <tbody>
                {lb.echeances.map((e) => (
                  <tr key={e.id} className={cn('border-b border-border last:border-0', e.statut === 'PAYEE' && 'opacity-60')}>
                    <td className="px-3 py-1.5">{e.numero}</td>
                    <td className="px-3 py-1.5 tabular-nums">{e.date}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatDZD(Number(e.montant), { decimals: 0 })}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatDZD(Number(e.part_capital), { decimals: 0 })}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-content-secondary">{formatDZD(Number(e.part_interets), { decimals: 0 })}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-content-muted">{formatDZD(Number(e.capital_restant), { decimals: 0 })}</td>
                    <td className="px-3 py-1.5">{e.statut === 'PAYEE' ? <span className="text-status-done">Payée</span> : <span className="text-content-muted">À venir</span>}</td>
                    <td className="px-3 py-1.5 text-right">{perms.canModifier && e.statut !== 'PAYEE' && <Button size="sm" variant="ghost" className="h-7 text-xs" loading={payMut.isPending} onClick={() => payMut.mutate(e.id)}>Comptabiliser</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
